import type { WritableComputedRef } from "vue";
import { computed, ref } from "vue";
import type { MaybeRef } from "@vueuse/core";
import type {
  FormItems,
  OperatingParameters,
  WellProperties
} from "./formTypes";

import {
  Well_Test_Gas_Input_Options_Enum,
  Well_Test_Liquid_Input_Options_Enum,
  Well_Test_Pressure_Input_Options_Enum
} from "gql/api";
import { DensityUnits } from "@/libs/units/density";
import { convert } from "@/libs/units";
import { GasFlowRateUnits } from "@/libs/units/flowRate";
import { clamp } from "@/utils/utils";
import {
  computePressureFromDepthPsia,
  computeFluidLevelFeet
} from "@/utils/computations";
/*

useWellTestInput

Composable which handles the reactive logic for user input to
Well Test flow rates and pressure. Flow Rates must be recalculated based on
each other and which "Input Type" has been selected by the user.

e.g. If the user has an Input Type of "Total Liquid Rate / Water Cut"
then the form should reactively calculate Oil Rate and Water Rate whenever
either Total Liquid Rate or Water Cut inputs are updated.

*/

export type WellTestData = {
  gasFlowRateMscf: WritableComputedRef<number>;
  gasOilRatioScfStb: WritableComputedRef<number>;
  gasLiquidRatioScfStb: WritableComputedRef<number>;
  oilFlowRateStbd: WritableComputedRef<number>;
  waterFlowRateStbd: WritableComputedRef<number>;
  totalLiquidFlowRateStbd: WritableComputedRef<number>;
  waterCutPercentage: WritableComputedRef<number>;
  producingBottomholePressurePsia: WritableComputedRef<number>;
  staticBottomholePressurePsia: WritableComputedRef<number>;
  productivityIndexStbdPsia: WritableComputedRef<number>;
  wellProductivityIndexStbdPsiaForDisplay: WritableComputedRef<number>;
  datumPointFt: WritableComputedRef<number>;
  staticFluidLevelFeet: WritableComputedRef<number>;
  producingFluidLevelFeet: WritableComputedRef<number>;
  liquidDensityLbmFt3: WritableComputedRef<number>;
};

export const useWellTestInput = function <
  T extends FormItems["wellProperties"] | FormItems["performanceEvaluation"]
>(
  wellProperties: MaybeRef<WellProperties>,
  parameters: MaybeRef<OperatingParameters>,
  form?: T
): WellTestData {
  const _wellProperties = ref(wellProperties);
  const _parameters = ref(parameters);

  const gasFlowRateMscf = computed({
    get: () => {
      switch (form?.data.value?.__typename) {
        case "scenario_well_properties":
          return form?.data.value.well_test_gas_rate_mscf_day || 0;
        case "performance_evaluations":
          return form?.data.value.gas_rate_mscf_day || 0;
        default:
          return _wellProperties.value?.well_test_gas_rate_mscf_day || 0;
      }
    },
    set: (val: number) => {
      switch (form?.data.value?.__typename) {
        case "scenario_well_properties":
          form?.update({
            well_test_gas_rate_mscf_day: val
          });
          break;
        case "performance_evaluations":
          form?.update({
            gas_rate_mscf_day: val
          });
      }
    }
  });

  // Preserves GOR and GLR when Oil Rate is updated
  const oilFlowRateStbd = computed({
    get: () => {
      switch (form?.data.value?.__typename) {
        case "scenario_well_properties":
          return form?.data.value.well_test_oil_rate_stb_day || 0;
        case "performance_evaluations":
          return form?.data.value.oil_rate_std_barrel_day || 0;
        default:
          return _wellProperties.value?.well_test_oil_rate_stb_day || 0;
      }
    },
    set: (val: number) => {
      const gor = gasOilRatioScfStb.value;
      const glr = gasLiquidRatioScfStb.value;

      switch (form?.data.value?.__typename) {
        case "scenario_well_properties":
          form?.update({
            well_test_oil_rate_stb_day: val
          });
          break;
        case "performance_evaluations":
          form?.update({
            oil_rate_std_barrel_day: val
          });
      }

      if (
        _wellProperties.value?.well_test_gas_input_type ===
        Well_Test_Gas_Input_Options_Enum.GasOilRatio
      ) {
        gasOilRatioScfStb.value = gor;
      } else if (
        _wellProperties.value?.well_test_gas_input_type ===
        Well_Test_Gas_Input_Options_Enum.GasLiquidRatio
      ) {
        gasLiquidRatioScfStb.value = glr;
      }
    }
  });

  // Preserves GLR when Water Rate is updated
  const waterFlowRateStbd = computed({
    get: () => {
      switch (form?.data.value?.__typename) {
        case "scenario_well_properties":
          return form?.data.value.well_test_water_rate_stb_day || 0;
        case "performance_evaluations":
          return form?.data.value.water_rate_std_barrel_day || 0;
        default:
          return _wellProperties.value?.well_test_water_rate_stb_day || 0;
      }
    },
    set: (val: number) => {
      const glr = gasLiquidRatioScfStb.value;

      switch (form?.data.value?.__typename) {
        case "scenario_well_properties":
          form?.update({
            well_test_water_rate_stb_day: val
          });
          break;
        case "performance_evaluations":
          form?.update({
            water_rate_std_barrel_day: val
          });
      }

      if (
        _wellProperties.value?.well_test_gas_input_type ===
        Well_Test_Gas_Input_Options_Enum.GasLiquidRatio
      ) {
        gasLiquidRatioScfStb.value = glr;
      }
    }
  });

  const producingBottomholePressurePsia = computed({
    get: () => {
      if (form?.data.value.__typename === "scenario_well_properties") {
        if (
          form?.data.value?.well_test_liquid_input_type ===
          Well_Test_Liquid_Input_Options_Enum.ProductivityIndexWaterCut
        ) {
          const pi = productivityIndexStbdPsia.value;
          const targetRate =
            _parameters.value?.target_liquid_rate_std_barrel_day || 1;
          const foo =
            staticBottomholePressurePsia.value - targetRate / pi >= 0
              ? staticBottomholePressurePsia.value - targetRate / pi
              : 0;
          return foo;
        }
        return (
          form.data.value?.well_test_producing_bottom_hole_pressure_psia || 0
        );
      }

      return (
        _wellProperties.value?.well_test_producing_bottom_hole_pressure_psia ||
        0
      );
    },
    set: (val: number) => {
      if (form?.data.value?.__typename === "scenario_well_properties") {
        form?.update({
          well_test_producing_bottom_hole_pressure_psia: val
        });
      }
    }
  });

  const staticBottomholePressurePsia = computed({
    get: () => {
      switch (form?.data.value?.__typename) {
        case "scenario_well_properties":
          return (
            form?.data.value.well_test_static_bottom_hole_pressure_psia || 0
          );
        default:
          return (
            _wellProperties.value?.well_test_static_bottom_hole_pressure_psia ||
            0
          );
      }
    },
    set: (val: number) => {
      if (form?.data.value?.__typename === "scenario_well_properties") {
        form.update({
          well_test_static_bottom_hole_pressure_psia: val
        });
      }
    }
  });

  const datumPointFt = computed({
    get: () => {
      return (
        _wellProperties.value?.well_test_datum_point_measured_depth_feet || 0
      );
    },
    set: (val: number) => {
      if (form?.data.value.__typename === "scenario_well_properties") {
        // Adjust static and fluid levels if pressure mode is fluid
        if (
          _wellProperties.value?.well_test_pressure_input_type ===
          Well_Test_Pressure_Input_Options_Enum.FluidLevel
        ) {
          staticFluidLevelFeet.value += datumPointFt.value - val;
          producingFluidLevelFeet.value += datumPointFt.value - val;
        }

        form.update({
          well_test_datum_point_measured_depth_feet: val
        });
      }
    }
  });

  const liquidDensityLbmFt3 = computed(() => {
    const waterCutRatio = waterCutPercentage.value / 100;

    const oilDensityLbmFt3 = convert(
      _wellProperties.value?.oil_gravity_api,
      DensityUnits.API,
      DensityUnits.PoundsPerCubicFoot
    );

    const waterDensityLbmFt3 = convert(
      _wellProperties.value?.water_specific_gravity,
      DensityUnits.SpecificGravity,
      DensityUnits.PoundsPerCubicFoot
    );

    return (
      oilDensityLbmFt3 * (1 - waterCutRatio) +
      waterDensityLbmFt3 * waterCutRatio
    );
  });

  // Derived from static pressure since that is what is persisted to the db
  const staticFluidLevelFeet = computed({
    get: () => {
      return computeFluidLevelFeet(
        datumPointFt.value,
        staticBottomholePressurePsia.value,
        liquidDensityLbmFt3.value
      );
    },
    set: (val: number) => {
      if (form?.data.value.__typename === "scenario_well_properties") {
        form.update({
          well_test_static_bottom_hole_pressure_psia:
            computePressureFromDepthPsia(
              datumPointFt.value,
              val,
              liquidDensityLbmFt3.value
            )
        });
      }
    }
  });

  // Derived from producing pressure since that is what is persisted to the db
  const producingFluidLevelFeet = computed({
    get: () => {
      return computeFluidLevelFeet(
        datumPointFt.value,
        producingBottomholePressurePsia.value,
        liquidDensityLbmFt3.value
      );
    },
    set: (val: number) => {
      if (form?.data.value.__typename === "scenario_well_properties") {
        form.update({
          well_test_producing_bottom_hole_pressure_psia:
            computePressureFromDepthPsia(
              datumPointFt.value,
              val,
              liquidDensityLbmFt3.value
            )
        });
      }
    }
  });

  // Only concerned with water/oil rates and water cut
  const totalLiquidFlowRateStbd = computed({
    get: () => {
      return oilFlowRateStbd.value + waterFlowRateStbd.value;
    },
    set: (val: number) => {
      const waterCutRatio = waterCutPercentage.value / 100;
      oilFlowRateStbd.value = val * (1 - waterCutRatio);
      waterFlowRateStbd.value = val * waterCutRatio;
    }
  });

  const safeRatio = (numerator: number, denominator: number): number => {
    if (
      !Number.isFinite(numerator) ||
      !Number.isFinite(denominator) ||
      denominator === 0
    ) {
      return 0;
    }

    const result = numerator / denominator;
    return Number.isFinite(result) ? result : 0;
  };

  // Uses gas and oil rates
  const gasOilRatioScfStb = computed({
    get: () => {
      const gasFlowRateScf = convert(
        gasFlowRateMscf.value,
        GasFlowRateUnits.ThousandCubicFeetPerDay,
        GasFlowRateUnits.CubicFeetPerDay
      );

      return safeRatio(gasFlowRateScf, oilFlowRateStbd.value);
    },
    set: (val: number) => {
      const gasFlowRateScf = val * oilFlowRateStbd.value;
      gasFlowRateMscf.value = convert(
        gasFlowRateScf,
        GasFlowRateUnits.CubicFeetPerDay,
        GasFlowRateUnits.ThousandCubicFeetPerDay
      );
    }
  });

  // Uses gas and total (oil + water) rate
  const gasLiquidRatioScfStb = computed({
    get: () => {
      const gasFlowRateScf = convert(
        gasFlowRateMscf.value,
        GasFlowRateUnits.ThousandCubicFeetPerDay,
        GasFlowRateUnits.CubicFeetPerDay
      );

      return safeRatio(gasFlowRateScf, totalLiquidFlowRateStbd.value);
    },
    set: (val: number) => {
      const gasFlowRateScf = val * totalLiquidFlowRateStbd.value;
      gasFlowRateMscf.value = convert(
        gasFlowRateScf,
        GasFlowRateUnits.CubicFeetPerDay,
        GasFlowRateUnits.ThousandCubicFeetPerDay
      );
    }
  });

  // Only changes water rate when oil rate/wc type is active since oil rate is active at that point
  // Changes water and oil rates when total rate/wc is active. This in turn updates the total rate
  const waterCutPercentage = computed({
    get: () => {
      const totalLiquidRate = totalLiquidFlowRateStbd.value;

      if (!Number.isFinite(totalLiquidRate) || totalLiquidRate <= 0) {
        return 0;
      }

      return (waterFlowRateStbd.value / totalLiquidRate) * 100 || 0;
    },
    set: (val: number) => {
      val = clamp(val, 0, 100);

      const waterCutRatio = val / 100;
      if (
        _wellProperties.value?.well_test_liquid_input_type ===
        "oil_rate_water_cut"
      ) {
        if (_wellProperties.value?.hps_liquid_type === "oil") {
          waterFlowRateStbd.value = 0;
          return;
        }

        waterFlowRateStbd.value =
          oilFlowRateStbd.value / (1 - waterCutRatio) - oilFlowRateStbd.value;
      } else {
        const totalLiquidRate = totalLiquidFlowRateStbd.value;

        waterFlowRateStbd.value = waterCutRatio * totalLiquidRate;
        oilFlowRateStbd.value = (1 - waterCutRatio) * totalLiquidRate;
      }
    }
  });

  // Calculated using the total liquid rate when not in PI mode. For display only
  const wellProductivityIndexStbdPsiaForDisplay = computed(() => {
    const pressureDeltaPSIA =
      staticBottomholePressurePsia.value -
      producingBottomholePressurePsia.value;

    if (!Number.isFinite(pressureDeltaPSIA) || pressureDeltaPSIA === 0) {
      return 0;
    }

    const productivityIndex = totalLiquidFlowRateStbd.value / pressureDeltaPSIA;

    return Number.isFinite(productivityIndex) ? productivityIndex : 0;
  });

  const productivityIndexStbdPsia = computed({
    get: () => {
      return _wellProperties.value?.well_test_productivity_index || 0;
    },
    set: (val: number) => {
      // // Use target rate to back calculate PBHP
      const targetRate =
        _parameters.value?.target_liquid_rate_std_barrel_day || 1;
      const pbhp = staticBottomholePressurePsia.value - targetRate / val;
      producingBottomholePressurePsia.value = Math.max(pbhp, 0);
      form?.update({
        well_test_productivity_index: val
      });
      // If there is no well rate given, set well rate to target rate
      // for the calcs down the road
      if (!totalLiquidFlowRateStbd.value) {
        totalLiquidFlowRateStbd.value = targetRate;
      }
    }
  });

  return {
    liquidDensityLbmFt3,
    gasFlowRateMscf,
    gasOilRatioScfStb,
    gasLiquidRatioScfStb,
    oilFlowRateStbd,
    waterFlowRateStbd,
    totalLiquidFlowRateStbd,
    waterCutPercentage,
    producingBottomholePressurePsia,
    staticBottomholePressurePsia,
    productivityIndexStbdPsia,
    wellProductivityIndexStbdPsiaForDisplay,
    datumPointFt,
    staticFluidLevelFeet,
    producingFluidLevelFeet
  };
};
